// Web Audio graph root (RALLY_GOAL.md §12): one AudioContext, created/resumed on the first user
// gesture, with buses for engine, effects, ambience and the co-driver. The engine bus is ducked
// while the co-driver speaks.

export type BusName = 'engine' | 'effects' | 'ambience' | 'codriver';

export interface Volumes {
  master: number;
  engine: number;
  effects: number;
  codriver: number;
}

export class AudioMixer {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  readonly buses = {} as Record<BusName, GainNode>;
  private readonly duckNode = {} as { gain: GainNode };
  private volumes: Volumes = { master: 0.55, engine: 0.6, effects: 0.55, codriver: 1 };
  /** Set when audio could not start (the game carries on with subtitles). */
  failed = false;
  private readonly listeners: ((ctx: AudioContext) => void)[] = [];

  /** Call from a user-gesture handler; safe to call repeatedly. */
  unlock(): AudioContext | null {
    if (this.failed) return null;
    try {
      if (!this.ctx) this.build();
      if (this.ctx && this.ctx.state !== 'running') void this.ctx.resume().catch(() => undefined);
    } catch (e) {
      console.warn('[audio] unavailable; continuing with subtitles only', e);
      this.failed = true;
      return null;
    }
    return this.ctx;
  }

  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  /** Run `fn` once the context exists (immediately if it already does). */
  onReady(fn: (ctx: AudioContext) => void): void {
    if (this.ctx) fn(this.ctx);
    else this.listeners.push(fn);
  }

  private build(): void {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) throw new Error('Web Audio API not supported');
    const ctx = new Ctor({ latencyHint: 'interactive' });
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -4;
    limiter.knee.value = 2;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.12;
    limiter.connect(ctx.destination);
    const master = ctx.createGain();
    master.connect(limiter);
    const duck = ctx.createGain();
    duck.connect(master);
    this.duckNode.gain = duck;
    for (const name of ['engine', 'effects', 'ambience', 'codriver'] as BusName[]) {
      const g = ctx.createGain();
      g.connect(name === 'engine' ? duck : master);
      this.buses[name] = g;
    }
    this.ctx = ctx;
    this.master = master;
    this.applyVolumes();
    for (const fn of this.listeners.splice(0)) fn(ctx);
  }

  setVolumes(v: Partial<Volumes>): void {
    this.volumes = { ...this.volumes, ...v };
    this.applyVolumes();
  }

  private applyVolumes(): void {
    if (!this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.volumes.master, t, 0.02);
    this.buses.engine.gain.setTargetAtTime(this.volumes.engine, t, 0.02);
    this.buses.effects.gain.setTargetAtTime(this.volumes.effects, t, 0.02);
    this.buses.ambience.gain.setTargetAtTime(this.volumes.effects * 0.8, t, 0.02);
    this.buses.codriver.gain.setTargetAtTime(this.volumes.codriver, t, 0.02);
  }

  /** Duck the engine bus under the co-driver: `active` true while a call is being spoken. */
  duck(active: boolean, until?: number): void {
    if (!this.ctx) return;
    const g = this.duckNode.gain.gain;
    const t = this.ctx.currentTime;
    g.cancelScheduledValues(t);
    g.setTargetAtTime(active ? 0.45 : 1, t, active ? 0.03 : 0.25);
    if (active && until !== undefined) g.setTargetAtTime(1, until, 0.25);
  }

  /** Suspend everything (pause menu / hidden tab). */
  suspend(): void {
    if (this.ctx?.state === 'running') void this.ctx.suspend().catch(() => undefined);
  }

  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume().catch(() => undefined);
  }
}
